import { useEffect, useState } from "react";
import { api, fmtCurrency } from "@/lib/api";
import { Receipt, AlertTriangle, CheckCircle2 } from "lucide-react";

export default function VatPage() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/dashboard").then(r => setD(r.data)); }, []);
  if (!d) return <div>Lade…</div>;
  const v = d.vat;

  return (
    <div className="space-y-6" data-testid="vat-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-semibold">Umsatzsteuer</div>
        <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">USt-Rücklage</h1>
        <p className="text-sm text-neutral-500 mt-2">Damit deine USt-Vorauszahlung dich nicht überrascht.</p>
      </div>

      <div className="rounded-xl border border-neutral-200 bg-white p-8 grid md:grid-cols-2 gap-8">
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Receipt className="h-5 w-5" />
            <span className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 font-semibold">Empfehlung</span>
          </div>
          <p className="text-sm text-neutral-600 mb-2">Für Umsatzsteuer solltest du aktuell mindestens zurücklegen:</p>
          <div className="font-display text-5xl font-semibold tracking-tighter" data-testid="vat-recommended">
            {fmtCurrency(v.estimated_liability, d.currency)}
          </div>
          {v.kleinunternehmer && (
            <div className="mt-4 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              Du bist Kleinunternehmer (§19 UStG) — es fallen aktuell keine Umsatzsteuer-Vorauszahlungen an.
            </div>
          )}
        </div>

        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 font-semibold mb-3">Rücklagensituation</div>
          <Row label="Auf Steuerkonto" value={fmtCurrency(v.current_tax_account, d.currency)} />
          <Row label="Empfohlene Rücklage" value={fmtCurrency(v.estimated_liability, d.currency)} />
          <div className="mt-3 pt-3 border-t border-neutral-200">
            {v.reserve_gap > 0 ? (
              <div className="flex items-center gap-3 text-red-600">
                <AlertTriangle className="h-5 w-5" />
                <div>
                  <div className="text-sm font-medium">Lücke</div>
                  <div className="font-display text-2xl font-semibold" data-testid="vat-gap">{fmtCurrency(v.reserve_gap, d.currency)}</div>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3 text-emerald-600">
                <CheckCircle2 className="h-5 w-5" />
                <div>
                  <div className="text-sm font-medium">Vollständig gedeckt</div>
                  <div className="font-display text-2xl font-semibold" data-testid="vat-coverage">+ {fmtCurrency(v.current_tax_account - v.estimated_liability, d.currency)}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Block title="Vereinnahmt vs. Vorsteuer">
          <Row label="Vereinnahmte USt (aus Kundenzahlungen)" value={fmtCurrency(v.vat_received, d.currency)} />
          {v.method === "soll" && <Row label="USt aus offenen Ausgangsrechnungen" value={fmtCurrency(v.vat_pending_from_open, d.currency)} />}
          <Row label="Gezahlte Vorsteuer (Eingangsrechnungen)" value={`- ${fmtCurrency(v.vat_paid, d.currency)}`} />
          <Row label="Offene Vorsteuer" value={fmtCurrency(v.vat_pending_in, d.currency)} muted />
          <div className="border-t border-neutral-200 mt-2 pt-2 flex justify-between text-sm font-semibold">
            <span>Geschätzte Zahllast</span><span className="tabular-nums">{fmtCurrency(v.estimated_liability, d.currency)}</span>
          </div>
        </Block>
        <Block title="Einstellungen">
          <Row label="USt-Methode" value={v.method === "ist" ? "Ist-Versteuerung" : "Soll-Versteuerung"} />
          <Row label="Kleinunternehmer" value={v.kleinunternehmer ? "Ja" : "Nein"} />
          <p className="text-xs text-neutral-500 mt-3">
            Du kannst diese Werte in „Einstellungen“ ändern.
          </p>
        </Block>
      </div>
    </div>
  );
}

function Row({ label, value, muted }) {
  return (
    <div className={`flex justify-between py-1.5 text-sm ${muted ? "text-neutral-500" : "text-neutral-700"}`}>
      <span>{label}</span><span className="tabular-nums font-medium">{value}</span>
    </div>
  );
}
function Block({ title, children }) {
  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-6">
      <div className="text-[11px] uppercase tracking-[0.18em] text-neutral-500 font-semibold mb-3">{title}</div>
      {children}
    </div>
  );
}
